import { z } from "zod";

export const BillingItemTypeSchema = z.enum([
  "appointment",
  "membership",
  "package",
  "fee",
  // Bought at the desk with no visit attached (a product, a standalone
  // service). It used to arrive labelled "appointment" and sat under the
  // appointments total.
  "purchase",
]);

export const BillingItemStateSchema = z.enum([
  "outstanding",
  "partial",
  "paid",
  "covered_by_insurance",
  "covered_by_membership",
  "insurance_pending",
  // Delivered and billed to nobody: there was never anything to settle.
  "no_charge",
  // Returned or refunded in full: nothing is owed, and nothing was kept.
  "returned",
]);

export const PayTargetSchema = z.object({
  receivablePlanId: z.string().nullable(),
  sourceRowId: z.string(),
  outstandingCents: z.number().int().nonnegative(),
});

export const PendingPaymentLinkSchema = z.object({
  collectionIntentId: z.string(),
  url: z.string().url().nullable(),
  provider: z.string().nullable(),
  providerFlow: z.string().nullable(),
  amountCents: z.number().int().nonnegative(),
  createdAt: z.number().nullable(),
});

export const BillingLineSchema = z.object({
  lineId: z.string(),
  title: z.string(),
  kind: z.string(),
  quantity: z.number().nonnegative(),
  totalCents: z.number().int().nonnegative(),
  yourPortionCents: z.number().int().nonnegative(),
  coveredByInsuranceCents: z.number().int().nonnegative(),
  coveredByMembershipCents: z.number().int().nonnegative(),
});

export const BillingItemSchema = z.object({
  itemId: z.string(),
  type: BillingItemTypeSchema,
  title: z.string(),
  subtitle: z.string().nullable(),
  occurredAt: z.number(),
  // The contract's states (BillingItemStateSchema), read tolerantly: a state
  // this app does not know yet shows under a neutral label instead of failing
  // the whole statement (memberships audit C37). See `states.ts`.
  state: z.string(),
  // The currency the bill is kept in.
  currency: z.string().optional().default("CAD"),
  amounts: z.object({
    totalCents: z.number().int().nonnegative(),
    paidCents: z.number().int().nonnegative(),
    // The whole debt, financed or not.
    outstandingCents: z.number().int().nonnegative(),
    coveredByInsuranceCents: z.number().int().nonnegative(),
    coveredByMembershipCents: z.number().int().nonnegative(),
    insurerOutstandingCents: z.number().int().nonnegative().default(0),
    // What can be paid from here now — the ONE figure every surface offers to
    // pay: the hero, Pay all, the checkboxes, the drawer, a guardian's screen.
    payableNowCents: z.number().int().nonnegative(),
  }),
  lines: z.array(BillingLineSchema).optional().default([]),
  payTargets: z.array(PayTargetSchema),
  pendingPaymentLink: PendingPaymentLinkSchema.nullable().optional(),
  // Set when a payment plan collects this debt. Its installments are
  // collected by the plan itself, so the item is shown but not payable here —
  // with what the plan collects next, and when.
  paymentPlan: z
    .object({
      paymentPlanId: z.string(),
      status: z.string(),
      nextInstallmentCents: z.number().int().nonnegative(),
      nextChargeAt: z.number().nullable(),
      installmentsLeft: z.number().int().nonnegative(),
    })
    .nullable()
    .optional(),
  metadata: z
    .object({
      appointmentId: z.string().nullable().optional(),
      providerName: z.string().nullable().optional(),
      locationName: z.string().nullable().optional(),
    })
    .default({}),
});

export const PaymentConfigSchema = z.object({
  success: z.literal(true),
  stripePublicKey: z.string(),
  stripeKeyScope: z.enum(["organization", "location"]),
  locationId: z.string().nullable(),
  currency: z.string(),
});

export const DependentSchema = z.object({
  pid: z.string(),
  fName: z.string().optional(),
  lName: z.string().optional(),
  email: z.string().nullable().optional(),
  phone: z.string().nullable().optional(),
  dob: z.union([z.string(), z.number()]).nullable().optional(),
  lid: z.any().optional(),
});

export const BootstrapSchema = z.object({
  success: z.literal(true),
  db: z.string(),
  patient: z.record(z.any()),
  organization: z.record(z.any()).nullable(),
  locations: z.array(z.record(z.any())),
  selectedLocation: z.string().nullable(),
  homeLocation: z.record(z.any()).nullable(),
  userType: z.string(),
  dependents: z.array(DependentSchema).optional().default([]),
});

export const PaymentMethodSchema = z.object({
  id: z.string(),
  brand: z.string().nullable(),
  last4: z.string().nullable(),
  expMonth: z.number().nullable(),
  expYear: z.number().nullable(),
  funding: z.string().nullable(),
  country: z.string().nullable(),
  isDefault: z.boolean(),
  createdAt: z.number().nullable(),
  // The name the PATIENT gave this card. Declared here or it does not exist:
  // these schemas are plain `z.object` with no `.passthrough()`, so anything
  // the server sends that is not named is deleted by `.parse` without an
  // error. Optional so a server that has not shipped the field yet still
  // parses. The clinic's own label for the same card is a separate value that
  // this app is never sent.
  nickname: z.string().nullable().optional(),
});

export const SetCardNicknameResponseSchema = z.object({
  success: z.literal(true),
  paymentMethodId: z.string(),
  nickname: z.string().nullable(),
});

export const PaymentMethodsResponseSchema = z.object({
  success: z.literal(true),
  methods: z.array(PaymentMethodSchema),
  defaultPaymentMethodId: z.string().nullable(),
});

export const SetupIntentResponseSchema = z.object({
  success: z.literal(true),
  clientSecret: z.string(),
  setupIntentId: z.string(),
});

export const BillingItemsResponseSchema = z.object({
  success: z.literal(true),
  patientId: z.string(),
  currency: z.string(),
  isDependentView: z.boolean().optional().default(false),
  totals: z.object({
    // The whole debt.
    outstandingCents: z.number().int().nonnegative(),
    // What "Pay all" pays: Σ payable now.
    payableNowCents: z.number().int().nonnegative(),
    // What payment plans collect instead.
    onPaymentPlan: z.object({
      itemCount: z.number().int().nonnegative(),
      outstandingCents: z.number().int().nonnegative(),
      nextInstallmentCents: z.number().int().nonnegative(),
      nextChargeAt: z.number().nullable(),
    }),
    byType: z.object({
      appointment: z.number().int().nonnegative(),
      membership: z.number().int().nonnegative(),
      package: z.number().int().nonnegative(),
      fee: z.number().int().nonnegative(),
      purchase: z.number().int().nonnegative().optional(),
    }),
  }),
  items: z.array(BillingItemSchema),
  // Items the server sent that this app could not read. Each item is read on
  // its own, so one it cannot read never blanks the others — and the screen
  // says some are missing rather than pretending there is nothing.
  unreadableItemCount: z.number().int().nonnegative().default(0),
});

/**
 * The statement, read item by item (audit C37): the envelope must be right,
 * each item is taken or counted as unreadable on its own.
 */
export function parseBillingItemsResponse(data: unknown): BillingItemsResponse {
  const envelope = BillingItemsResponseSchema.omit({ items: true, unreadableItemCount: true })
    .extend({ items: z.array(z.unknown()) })
    .parse(data);
  const items: BillingItem[] = [];
  let unreadableItemCount = 0;
  for (const raw of envelope.items) {
    const parsed = BillingItemSchema.safeParse(raw);
    if (parsed.success) items.push(parsed.data);
    else unreadableItemCount += 1;
  }
  return { ...envelope, items, unreadableItemCount };
}

export const BillingDocumentSchema = z.object({
  documentId: z.string(),
  kind: z.string(),
  status: z.string(),
  currency: z.string(),
  totalCents: z.number().int().nonnegative(),
  issuedAt: z.number(),
  appointmentId: z.string().nullable(),
  receivablePlanId: z.string().nullable(),
  orderId: z.string().nullable(),
  paymentMethodSummary: z.array(
    z.object({
      method: z.string().nullable(),
      amountCents: z.number().int().nonnegative(),
      paidAt: z.number(),
    })
  ),
  hasFile: z.boolean(),
});

export const BillingDocumentsResponseSchema = z.object({
  success: z.literal(true),
  documents: z.array(BillingDocumentSchema),
});

export const DocumentDownloadResponseSchema = z.object({
  success: z.literal(true),
  url: z.string().url(),
  expiresInSeconds: z.number().int().positive(),
  filename: z.string(),
});

export const CollectRequestTargetSchema = z.object({
  itemId: z.string(),
  receivablePlanId: z.string().nullable(),
  sourceRowId: z.string(),
  expectedOutstandingCents: z.number().int().nonnegative(),
  payAmountCents: z.number().int().positive(),
});

export const CollectResponseSchema = z.object({
  success: z.literal(true),
  paymentBatchId: z.string(),
  paymentIntentId: z.string(),
  clientSecret: z.string(),
  totalCents: z.number().int().nonnegative(),
  currency: z.string(),
  collectionIntentIds: z.array(z.string()),
  status: z.string(),
});

export type BillingItem = z.infer<typeof BillingItemSchema>;
export type BillingLine = z.infer<typeof BillingLineSchema>;
export type BillingItemState = z.infer<typeof BillingItemStateSchema>;
export type BillingItemType = z.infer<typeof BillingItemTypeSchema>;
export type BillingItemsResponse = z.infer<typeof BillingItemsResponseSchema>;
export type PayTarget = z.infer<typeof PayTargetSchema>;
export type CollectRequestTarget = z.infer<typeof CollectRequestTargetSchema>;
export type CollectResponse = z.infer<typeof CollectResponseSchema>;
export type PendingPaymentLink = z.infer<typeof PendingPaymentLinkSchema>;
export type PaymentConfig = z.infer<typeof PaymentConfigSchema>;
export type BootstrapResponse = z.infer<typeof BootstrapSchema>;
export type Dependent = z.infer<typeof DependentSchema>;
export type PaymentMethod = z.infer<typeof PaymentMethodSchema>;
export type PaymentMethodsResponse = z.infer<typeof PaymentMethodsResponseSchema>;
export type SetupIntentResponse = z.infer<typeof SetupIntentResponseSchema>;
export type SetCardNicknameResponse = z.infer<
  typeof SetCardNicknameResponseSchema
>;
export type BillingDocument = z.infer<typeof BillingDocumentSchema>;
export type BillingDocumentsResponse = z.infer<typeof BillingDocumentsResponseSchema>;
export type DocumentDownloadResponse = z.infer<typeof DocumentDownloadResponseSchema>;
